import { baseApi } from './baseApi';

export const hiveLinkApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // CHANNELS
    hlListChannels: builder.query({
      query: (params) => ({ url: '/hivelink/channels', params }),
      providesTags: ['Channel'],
      transformResponse: (r) => r.data,
    }),
    hlGetChannel: builder.query({
      query: (id) => `/hivelink/channels/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Channel', id }],
      transformResponse: (r) => r.data,
    }),
    hlCreateChannel: builder.mutation({
      query: (body) => ({ url: '/hivelink/channels', method: 'POST', body }),
      invalidatesTags: ['Channel'],
      transformResponse: (r) => r.data,
    }),
    hlUpdateChannel: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/hivelink/channels/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Channel', id }, 'Channel'],
    }),
    hlDeleteChannel: builder.mutation({
      query: (id) => ({ url: `/hivelink/channels/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Channel'],
    }),
    // Archive / restore. The danger-zone button used a bare fetch() — no auth
    // header, no CSRF token, no cache invalidation, errors swallowed — so on a
    // server with global CSRF + Bearer auth it was silently failing. Going
    // through the base query fixes auth + CSRF, and 'Channel' invalidation now
    // actually removes the channel from the sidebar (the list query was also
    // taught to hide isArchived — see server listChannels).
    hlArchiveChannel: builder.mutation({
      query: (channelId) => ({ url: `/hivelink/channels/${channelId}/archive`, method: 'POST' }),
      transformResponse: (r) => r?.data,
      invalidatesTags: (_r, _e, channelId) => [{ type: 'Channel', id: channelId }, 'Channel'],
    }),
    hlUnarchiveChannel: builder.mutation({
      query: (channelId) => ({ url: `/hivelink/channels/${channelId}/unarchive`, method: 'POST' }),
      transformResponse: (r) => r?.data,
      invalidatesTags: (_r, _e, channelId) => [{ type: 'Channel', id: channelId }, 'Channel'],
    }),
    // Restore/browse view — channel-level archived channels only.
    hlListArchivedChannels: builder.query({
      query: () => ({ url: '/hivelink/channels', params: { onlyArchived: true } }),
      transformResponse: (r) => r?.data || [],
      providesTags: ['Channel'],
    }),
    // Join / leave — the two actions that were missing entirely.
    hlJoinChannel: builder.mutation({
      query: (channelId) => ({ url: `/hivelink/channels/${channelId}/join`, method: 'POST' }),
      transformResponse: (r) => r?.data,
      invalidatesTags: ['Channel'],
    }),
    hlLeaveChannel: builder.mutation({
      query: (channelId) => ({ url: `/hivelink/channels/${channelId}/leave`, method: 'POST' }),
      transformResponse: (r) => r?.data,
      invalidatesTags: ['Channel'],
    }),

    // ── Your own account ──
    hlUpdateDeepWork: builder.mutation({
      query: (body) => ({ url: '/hivelink/me/deep-work', method: 'PUT', body }),
      transformResponse: (r) => r?.data,
      invalidatesTags: ['DeepWork'],
    }),
    hlLeaveAllChannels: builder.mutation({
      query: () => ({ url: '/hivelink/me/leave-all', method: 'POST' }),
      transformResponse: (r) => r?.data,
      invalidatesTags: ['Channel'],
    }),
    hlExportMyData: builder.query({
      query: () => '/hivelink/me/export',
      transformResponse: (r) => r?.data,
    }),
    hlResetMyKeys: builder.mutation({
      query: () => ({ url: '/hivelink/me/reset-keys', method: 'POST' }),
      transformResponse: (r) => r?.data,
      invalidatesTags: ['Channel'],
    }),
    hlDeleteMyData: builder.mutation({
      query: () => ({ url: '/hivelink/me/delete-data', method: 'POST', body: { confirm: 'DELETE' } }),
      transformResponse: (r) => r?.data,
      invalidatesTags: [{ type: 'HLAISettings', id: 'user' }, 'SavedReply'],
    }),

    // ── Zuno settings ──
    // scope 'user' returns the caller's own prefs (recap time); omitting it
    // returns the workspace policy (toxicity, interrupt).
    hlGetAISettings: builder.query({
      query: (scope) => ({ url: '/hivelink/ai/settings', params: scope ? { scope } : undefined }),
      transformResponse: (r) => r?.data,
      providesTags: (res, err, scope) => [{ type: 'HLAISettings', id: scope || 'workspace' }],
    }),
    hlUpdateAISettings: builder.mutation({
      query: ({ scope, ...patch }) => ({
        url: '/hivelink/ai/settings', method: 'PUT',
        params: scope ? { scope } : undefined, body: patch,
      }),
      transformResponse: (r) => r?.data,
      invalidatesTags: (res, err, { scope }) => [{ type: 'HLAISettings', id: scope || 'workspace' }],
    }),

    // ── Canvas templates ──
    hlListCanvasTemplates: builder.query({
      query: () => '/hivelink/canvas-templates',
      transformResponse: (r) => r?.data || [],
      providesTags: ['HLCanvasTemplate'],
    }),
    hlCreateCanvasTemplate: builder.mutation({
      query: (body) => ({ url: '/hivelink/canvas-templates', method: 'POST', body }),
      transformResponse: (r) => r?.data,
      invalidatesTags: ['HLCanvasTemplate'],
    }),
    hlUpdateCanvasTemplate: builder.mutation({
      query: ({ templateId, ...body }) => ({
        url: `/hivelink/canvas-templates/${templateId}`, method: 'PUT', body,
      }),
      transformResponse: (r) => r?.data,
      invalidatesTags: ['HLCanvasTemplate'],
    }),
    hlDeleteCanvasTemplate: builder.mutation({
      query: (templateId) => ({ url: `/hivelink/canvas-templates/${templateId}`, method: 'DELETE' }),
      transformResponse: (r) => r?.data,
      invalidatesTags: ['HLCanvasTemplate'],
    }),
    // Creates the canvas and counts the use in one call, so the two cannot drift.
    hlUseCanvasTemplate: builder.mutation({
      query: ({ templateId, ...body }) => ({
        url: `/hivelink/canvas-templates/${templateId}/use`, method: 'POST', body,
      }),
      transformResponse: (r) => r?.data,
      invalidatesTags: ['HLCanvasTemplate', { type: 'HLCanvas', id: 'LIST' }],
    }),

    // ── Canvas ──
    // Everything reachable, for the Collaboration view. Tagged 'LIST' so any
    // canvas mutation refreshes it without naming a channel.
    hlListMyCanvases: builder.query({
      query: () => '/hivelink/canvases/mine',
      transformResponse: (r) => r?.data || [],
      providesTags: [{ type: 'HLCanvas', id: 'LIST' }],
    }),
    hlListCanvases: builder.query({
      query: (channelId) => `/hivelink/channels/${channelId}/canvases`,
      transformResponse: (r) => r?.data || [],
      providesTags: (_r, _e, channelId) => [{ type: 'HLCanvas', id: channelId }],
    }),
    hlGetCanvas: builder.query({
      query: (canvasId) => `/hivelink/canvases/${canvasId}`,
      transformResponse: (r) => r?.data || null,
      providesTags: (_r, _e, canvasId) => [{ type: 'HLCanvas', id: canvasId }],
    }),
    hlCreateCanvas: builder.mutation({
      query: ({ channelId, ...body }) => ({
        url: `/hivelink/channels/${channelId}/canvases`, method: 'POST', body,
      }),
      transformResponse: (r) => r?.data,
      invalidatesTags: (_r, _e, { channelId }) => [
        { type: 'HLCanvas', id: 'LIST' },{ type: 'HLCanvas', id: channelId }],
    }),
    hlUpdateCanvas: builder.mutation({
      query: ({ canvasId, ...body }) => ({
        url: `/hivelink/canvases/${canvasId}`, method: 'PUT', body,
      }),
      // Both the document and the channel's list carry the title.
      transformResponse: (r) => r?.data,
      invalidatesTags: (_r, _e, { canvasId, channelId }) => [
        { type: 'HLCanvas', id: 'LIST' },
        { type: 'HLCanvas', id: canvasId }, { type: 'HLCanvas', id: channelId },
      ],
    }),
    hlToggleCanvasItem: builder.mutation({
      query: ({ canvasId, index }) => ({
        url: `/hivelink/canvases/${canvasId}/items/${index}/toggle`, method: 'POST',
      }),
      transformResponse: (r) => r?.data,
      invalidatesTags: (_r, _e, { canvasId }) => [
        { type: 'HLCanvas', id: 'LIST' },{ type: 'HLCanvas', id: canvasId }],
    }),
    hlDeleteCanvas: builder.mutation({
      query: ({ canvasId }) => ({ url: `/hivelink/canvases/${canvasId}`, method: 'DELETE' }),
      transformResponse: (r) => r?.data,
      invalidatesTags: (_r, _e, { channelId }) => [
        { type: 'HLCanvas', id: 'LIST' },{ type: 'HLCanvas', id: channelId }],
    }),

    // ── Channel invitation templates ──
    hlListInviteTemplates: builder.query({
      query: () => '/hivelink/invite-templates',
      transformResponse: (r) => r?.data || [],
      providesTags: ['HLInviteTemplate'],
    }),
    hlCreateInviteTemplate: builder.mutation({
      query: (body) => ({ url: '/hivelink/invite-templates', method: 'POST', body }),
      invalidatesTags: ['HLInviteTemplate'],
    }),
    hlUpdateInviteTemplate: builder.mutation({
      query: ({ templateId, ...body }) => ({
        url: `/hivelink/invite-templates/${templateId}`, method: 'PUT', body,
      }),
      invalidatesTags: ['HLInviteTemplate'],
    }),
    hlDeleteInviteTemplate: builder.mutation({
      query: (templateId) => ({ url: `/hivelink/invite-templates/${templateId}`, method: 'DELETE' }),
      invalidatesTags: ['HLInviteTemplate'],
    }),
    hlUseInviteTemplate: builder.mutation({
      query: (templateId) => ({ url: `/hivelink/invite-templates/${templateId}/use`, method: 'POST' }),
      invalidatesTags: ['HLInviteTemplate'],
    }),

    hlAddMembers: builder.mutation({
      // `note` is optional — the inviter's message, included in the email the
      // server sends to each person added.
      query: ({ channelId, memberIds, note }) => ({
        url: `/hivelink/channels/${channelId}/members`, method: 'POST', body: { memberIds, note },
      }),
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'Channel', id: channelId }],
    }),
    hlRemoveMember: builder.mutation({
      query: ({ channelId, userId }) => ({
        url: `/hivelink/channels/${channelId}/members/${userId}`, method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'Channel', id: channelId }],
    }),
    // Was a bare fetch() in MemberManagementPanel — no Authorization header, no
    // CSRF token — so on a server with global CSRF + Bearer auth the role change
    // was failing (401/403) and surfacing as "Failed to update role". Through the
    // base query it carries auth + CSRF, and 'Channel' invalidation refreshes the
    // member list.
    hlUpdateMemberRole: builder.mutation({
      query: ({ channelId, userId, role }) => ({
        url: `/hivelink/channels/${channelId}/members/${userId}/role`, method: 'PUT', body: { role },
      }),
      transformResponse: (r) => r?.data,
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'Channel', id: channelId }, 'Channel'],
    }),
    // Add-by-email: server adds existing workspace members straight to the
    // channel, or sends a workspace invite carrying this channel for new people.
    hlInviteByEmail: builder.mutation({
      query: ({ channelId, email }) => ({
        url: `/hivelink/channels/${channelId}/invite-by-email`, method: 'POST', body: { email },
      }),
      transformResponse: (r) => r?.data,
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'Channel', id: channelId }, 'Channel'],
    }),
    hlToggleStar: builder.mutation({
      query: (channelId) => ({ url: `/hivelink/channels/${channelId}/star`, method: 'POST' }),
      // Optimistic update — flip starred instantly without waiting for refetch
      async onQueryStarted(channelId, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          baseApi.util.updateQueryData('hlListChannels', {}, (draft) => {
            if (!Array.isArray(draft)) return;
            const ch = draft.find((c) => (c._id || c.id)?.toString() === channelId?.toString());
            if (ch) {
              if (!ch._memberSettings) ch._memberSettings = {};
              ch._memberSettings.starred = !ch._memberSettings.starred;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ['Channel'],
    }),
    hlUpdateNotifications: builder.mutation({
      query: ({ channelId, ...body }) => ({
        url: `/hivelink/channels/${channelId}/notifications`, method: 'PUT', body,
      }),
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'Channel', id: channelId }],
    }),
    hlGenerateExternalLink: builder.mutation({
      query: (channelId) => ({
        url: `/hivelink/channels/${channelId}/external-link`, method: 'POST',
      }),
      invalidatesTags: (_r, _e, channelId) => [{ type: 'Channel', id: channelId }],
    }),
    hlRevokeExternalLink: builder.mutation({
      query: (channelId) => ({
        url: `/hivelink/channels/${channelId}/external-link`, method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, channelId) => [{ type: 'Channel', id: channelId }],
    }),
    // E2E ENCRYPTION
    hlEnableE2E: builder.mutation({
      query: ({ channelId, ...body }) => ({ url: `/hivelink/channels/${channelId}/e2e/enable`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'Channel', id: channelId }],
    }),
    hlDisableE2E: builder.mutation({
      query: (channelId) => ({ url: `/hivelink/channels/${channelId}/e2e/disable`, method: 'POST' }),
      invalidatesTags: (_r, _e, channelId) => [{ type: 'Channel', id: channelId }],
    }),
    hlUploadE2EKey: builder.mutation({
      query: ({ channelId, ...body }) => ({ url: `/hivelink/channels/${channelId}/e2e/keys`, method: 'POST', body }),
    }),
    hlGetE2EKeys: builder.query({
      query: (channelId) => `/hivelink/channels/${channelId}/e2e/keys`,
      transformResponse: (r) => r.data,
    }),
    hlRotateE2EKey: builder.mutation({
      query: ({ channelId, ...body }) => ({ url: `/hivelink/channels/${channelId}/e2e/rotate`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'Channel', id: channelId }],
    }),
    // MESSAGES
    hlGetMessages: builder.query({
      query: ({ channelId, ...params }) => ({
        url: `/hivelink/channels/${channelId}/messages`, params,
      }),
      providesTags: (_r, _e, { channelId }) => [{ type: 'Message', id: channelId }],
      transformResponse: (r) => r.data,
    }),
    hlSendMessage: builder.mutation({
      query: ({ channelId, ...body }) => ({
        url: `/hivelink/channels/${channelId}/messages`, method: 'POST', body,
      }),
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'Message', id: channelId }, 'Message'],
      transformResponse: (r) => r.data,
    }),
    hlEditMessage: builder.mutation({
      query: ({ messageId, content }) => ({
        url: `/hivelink/messages/${messageId}`, method: 'PUT', body: { content },
      }),
      invalidatesTags: ['Message'],
    }),
    hlDeleteMessage: builder.mutation({
      query: (messageId) => ({ url: `/hivelink/messages/${messageId}`, method: 'DELETE' }),
      invalidatesTags: ['Message'],
    }),
    // THREADS
    hlGetThread: builder.query({
      query: ({ messageId, ...params }) => ({
        url: `/hivelink/messages/${messageId}/thread`, params,
      }),      providesTags: (_r, _e, { messageId }) => [{ type: 'Message', id: `thread-${messageId}` }],
      transformResponse: (r) => r.data,
    }),
    // CROSS-OBJECT CONTEXT (Feature 1)
    hlGetProjectContext: builder.query({
      query: (channelId) => `/hivelink/channels/${channelId}/project-context`,
      // Broad tags so any sprint/task/goal write in the app refreshes the card.
      providesTags: ['ProjectContext', 'Sprint', 'Task', 'Goal'],
      transformResponse: (r) => r.data,
    }),
    // AUTONOMOUS ZUNO (Feature 2)
    hlZunoResolve: builder.mutation({
      query: ({ messageId, actionIndex, decision }) => ({
        url: `/hivelink/messages/${messageId}/zuno/resolve`,
        method: 'POST',
        body: { actionIndex, decision },
      }),
      transformResponse: (r) => r.data,
    }),
    hlForwardMessage: builder.mutation({
      query: ({ messageId, targetChannelId, note }) => ({
        url: `/hivelink/messages/${messageId}/forward`,
        method: 'POST',
        body: { targetChannelId, note },
      }),
      transformResponse: (r) => r.data,
    }),
    hlGetObjectCard: builder.query({
      query: ({ type, id }) => `/hivelink/object-card?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`,
      transformResponse: (r) => r.data,
    }),
    hlMarkUnread: builder.mutation({
      query: ({ channelId, messageId }) => ({
        url: `/hivelink/channels/${channelId}/mark-unread`, method: 'POST', body: { messageId },
      }),
      transformResponse: (r) => r.data,
      invalidatesTags: ['Channel'],
    }),
    hlAddChannelBookmark: builder.mutation({
      query: ({ channelId, ...body }) => ({ url: `/hivelink/channels/${channelId}/bookmarks`, method: 'POST', body }),
      transformResponse: (r) => r.data,
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'Channel', id: channelId }],
    }),
    hlRemoveChannelBookmark: builder.mutation({
      query: ({ channelId, bookmarkId }) => ({ url: `/hivelink/channels/${channelId}/bookmarks/${bookmarkId}`, method: 'DELETE' }),
      transformResponse: (r) => r.data,
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'Channel', id: channelId }],
    }),
    hlGetThreadsInbox: builder.query({
      query: () => '/hivelink/threads-inbox',
      transformResponse: (r) => r.data,
      providesTags: ['ThreadsInbox'],
    }),
    hlGetSmartReplies: builder.query({
      query: ({ channelId, threadId }) => `/hivelink/channels/${channelId}/smart-replies${threadId ? `?threadId=${threadId}` : ''}`,
      transformResponse: (r) => r.data,
    }),
    hlCatchMeUp: builder.query({
      query: ({ channelId, threadId, hours }) => {
        const p = new URLSearchParams();
        if (threadId) p.set('threadId', threadId);
        if (hours) p.set('hours', hours);
        const qs = p.toString();
        return `/hivelink/channels/${channelId}/catch-up${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (r) => r.data,
    }),
    hlSearchProjects: builder.query({
      query: (q) => `/hivelink/project-search?q=${encodeURIComponent(q || '')}`,
      transformResponse: (r) => r.data,
    }),
    hlPostProjectBrief: builder.mutation({
      query: ({ channelId, projectId }) => ({ url: `/hivelink/channels/${channelId}/project-brief`, method: 'POST', body: { projectId } }),
      transformResponse: (r) => r.data,
    }),
    // COMPLIANT HUDDLES (Feature 3)
    hlGetHuddleCompliance: builder.query({
      query: (callId) => `/hivelink/huddles/${callId}/compliance`,
      providesTags: (_r, _e, callId) => [{ type: 'HuddleCompliance', id: callId }],
      transformResponse: (r) => r.data,
    }),
    hlSetHuddleLegalHold: builder.mutation({
      query: ({ callId, hold }) => ({ url: `/hivelink/huddles/${callId}/recording/legal-hold`, method: 'POST', body: { hold } }),
      invalidatesTags: (_r, _e, { callId }) => [{ type: 'HuddleCompliance', id: callId }],
      transformResponse: (r) => r.data,
    }),
    // PROVENANCE BACKLINK (Phase B)
    hlLinkEntity: builder.mutation({
      query: ({ messageId, ...body }) => ({
        url: `/hivelink/messages/${messageId}/link-entity`, method: 'POST', body,
      }),
      invalidatesTags: (_r, _e, { channelId }) => (channelId ? [{ type: 'Message', id: `channel-${channelId}` }] : []),
      transformResponse: (r) => r.data,
    }),
    hlUnlinkEntity: builder.mutation({
      query: ({ messageId, entityId }) => ({
        url: `/hivelink/messages/${messageId}/linked-entities/${entityId}`, method: 'DELETE',
      }),
      transformResponse: (r) => r.data,
    }),
    // REACTIONS
    hlToggleReaction: builder.mutation({
      query: ({ messageId, emoji }) => ({
        url: `/hivelink/messages/${messageId}/reactions`, method: 'POST', body: { emoji },
      }),
      invalidatesTags: ['Message'],
    }),
    // PIN / BOOKMARK
    hlPinMessage: builder.mutation({
      query: (messageId) => ({ url: `/hivelink/messages/${messageId}/pin`, method: 'POST' }),
      invalidatesTags: ['Message'],
    }),
    hlGetPinnedMessages: builder.query({
      query: (channelId) => `/hivelink/channels/${channelId}/pins`,
      providesTags: ['Message'],
      transformResponse: (r) => r.data,
    }),
    hlToggleBookmark: builder.mutation({
      query: (messageId) => ({ url: `/hivelink/messages/${messageId}/bookmark`, method: 'POST' }),
      invalidatesTags: ['Message'],
    }),
    hlGetBookmarks: builder.query({
      query: () => '/hivelink/bookmarks',
      providesTags: ['Message'],
      transformResponse: (r) => r.data,
    }),
    // READ TRACKING
    hlMarkRead: builder.mutation({
      query: ({ channelId, messageId }) => ({
        url: `/hivelink/channels/${channelId}/read`, method: 'POST', body: { messageId },
      }),
    }),
    hlGetUnreadCounts: builder.query({
      query: () => '/hivelink/unread',
      transformResponse: (r) => r.data,
    }),
    // SEARCH
    hlSearchMessages: builder.query({
      query: (params) => ({ url: '/hivelink/search', params }),
      transformResponse: (r) => r.data,
    }),
    // PRESENCE
    hlSetPresence: builder.mutation({
      query: (body) => ({ url: '/hivelink/presence', method: 'PUT', body }),
    }),
    hlGetBulkPresence: builder.mutation({
      query: (userIds) => ({ url: '/hivelink/presence/bulk', method: 'POST', body: { userIds } }),
    }),
    // UPLOAD
    hlUploadFiles: builder.mutation({
      query: (formData) => ({ url: '/hivelink/upload', method: 'POST', body: formData }),
      transformResponse: (r) => r.data,
    }),
    hlTranslateMessage: builder.mutation({
      query: ({ messageId, targetLang = 'en' }) => ({
        url: `/hivelink/messages/${messageId}/translate`, method: 'POST', body: { targetLang },
      }),
      transformResponse: (r) => r.data,
    }),
    hlSetReminder: builder.mutation({
      query: ({ messageId, ...body }) => ({
        url: `/hivelink/messages/${messageId}/reminder`, method: 'POST', body,
      }),
      transformResponse: (r) => r.data,
    }),
    hlGetReminders: builder.query({
      query: () => '/hivelink/reminders',
      transformResponse: (r) => r.data,
    }),
    // NOTIFICATIONS
    hlListNotifications: builder.query({
      query: (params) => ({ url: '/hivelink/notifications', params }),
      providesTags: ['Notification'],
      transformResponse: (r) => r.data,
    }),
    hlMarkNotificationRead: builder.mutation({
      query: (notificationId) => ({ url: `/hivelink/notifications/${notificationId}/read`, method: 'PUT' }),
      invalidatesTags: ['Notification'],
    }),
    hlMarkAllNotificationsRead: builder.mutation({
      query: () => ({ url: '/hivelink/notifications/read-all', method: 'PUT' }),
      invalidatesTags: ['Notification'],
    }),

    // ═══════════════════════════════════════════
    //  MESSAGE SCHEDULING
    // ═══════════════════════════════════════════
    hlScheduleMessage: builder.mutation({
      query: ({ channelId, ...body }) => ({
        url: `/hivelink/channels/${channelId}/messages/schedule`, method: 'POST', body,
      }),
      invalidatesTags: ['Message'],
      transformResponse: (r) => r.data,
    }),
    hlListScheduledMessages: builder.query({
      query: (channelId) => `/hivelink/channels/${channelId}/messages/scheduled`,
      providesTags: (_r, _e, channelId) => [{ type: 'Message', id: `sched-${channelId}` }],
      transformResponse: (r) => r.data,
    }),
    hlUpdateScheduledMessage: builder.mutation({
      query: ({ messageId, ...body }) => ({
        url: `/hivelink/messages/${messageId}/schedule`, method: 'PUT', body,
      }),
      invalidatesTags: ['Message'],
    }),
    hlCancelScheduledMessage: builder.mutation({
      query: (messageId) => ({
        url: `/hivelink/messages/${messageId}/schedule`, method: 'DELETE',
      }),
      invalidatesTags: ['Message'],
    }),

    // ═══════════════════════════════════════════
    //  POLLS & SURVEYS
    // ═══════════════════════════════════════════
    // Cross-channel list for the Collaboration view. 'LIST' so any poll
    // mutation refreshes it without naming a channel.
    hlListMyPolls: builder.query({
      query: () => '/hivelink/polls/mine',
      transformResponse: (r) => r?.data || [],
      providesTags: [{ type: 'Poll', id: 'LIST' }],
    }),
    hlCreatePoll: builder.mutation({
      query: ({ channelId, ...body }) => ({ url: `/hivelink/channels/${channelId}/polls`, method: 'POST', body }),
      invalidatesTags: ['Message', { type: 'Poll', id: 'LIST' }],
      transformResponse: (r) => r.data,
    }),
    hlListPolls: builder.query({
      query: ({ channelId, ...params }) => ({ url: `/hivelink/channels/${channelId}/polls`, params }),
      providesTags: (_r, _e, { channelId }) => [{ type: 'Message', id: `polls-${channelId}` }],
      transformResponse: (r) => r.data,
    }),
    hlGetPoll: builder.query({
      query: (pollId) => `/hivelink/polls/${pollId}`,
      providesTags: (_r, _e, pollId) => [{ type: 'Message', id: `poll-${pollId}` }],
      transformResponse: (r) => r.data,
    }),
    hlVotePoll: builder.mutation({
      query: ({ pollId, optionIndexes }) => ({ url: `/hivelink/polls/${pollId}/vote`, method: 'POST', body: { optionIndexes } }),
      invalidatesTags: ['Message', { type: 'Poll', id: 'LIST' }],
      transformResponse: (r) => r.data,
    }),
    hlAddPollOption: builder.mutation({
      query: ({ pollId, text }) => ({ url: `/hivelink/polls/${pollId}/options`, method: 'POST', body: { text } }),
      invalidatesTags: ['Message', { type: 'Poll', id: 'LIST' }],
      transformResponse: (r) => r.data,
    }),
    hlClosePoll: builder.mutation({
      query: (pollId) => ({ url: `/hivelink/polls/${pollId}/close`, method: 'POST' }),
      invalidatesTags: ['Message', { type: 'Poll', id: 'LIST' }],
      transformResponse: (r) => r.data,
    }),
    hlDeletePoll: builder.mutation({
      query: (pollId) => ({ url: `/hivelink/polls/${pollId}`, method: 'DELETE' }),
      invalidatesTags: ['Message', { type: 'Poll', id: 'LIST' }],
    }),

    // ═══════════════════════════════════════════
    //  CUSTOM EMOJIS
    // ═══════════════════════════════════════════
    hlUploadEmoji: builder.mutation({
      query: (body) => ({ url: '/hivelink/emojis', method: 'POST', body }),
      invalidatesTags: ['Emoji'],
      transformResponse: (r) => r.data,
    }),
    hlListEmojis: builder.query({
      query: (params) => ({ url: '/hivelink/emojis', params }),
      providesTags: ['Emoji'],
      transformResponse: (r) => r.data,
    }),
    hlGetEmojiCategories: builder.query({
      query: () => '/hivelink/emojis/categories',
      providesTags: ['Emoji'],
      transformResponse: (r) => r.data,
    }),
    hlUpdateEmoji: builder.mutation({
      query: ({ emojiId, ...body }) => ({ url: `/hivelink/emojis/${emojiId}`, method: 'PUT', body }),
      invalidatesTags: ['Emoji'],
    }),
    hlDeleteEmoji: builder.mutation({
      query: (emojiId) => ({ url: `/hivelink/emojis/${emojiId}`, method: 'DELETE' }),
      invalidatesTags: ['Emoji'],
    }),
    hlCreateEmojiPack: builder.mutation({
      query: (body) => ({ url: '/hivelink/emoji-packs', method: 'POST', body }),
      invalidatesTags: ['Emoji'],
      transformResponse: (r) => r.data,
    }),
    hlListEmojiPacks: builder.query({
      query: () => '/hivelink/emoji-packs',
      providesTags: ['Emoji'],
      transformResponse: (r) => r.data,
    }),
    hlUpdateEmojiPack: builder.mutation({
      query: ({ packId, ...body }) => ({ url: `/hivelink/emoji-packs/${packId}`, method: 'PUT', body }),
      invalidatesTags: ['Emoji'],
    }),
    hlDeleteEmojiPack: builder.mutation({
      query: (packId) => ({ url: `/hivelink/emoji-packs/${packId}`, method: 'DELETE' }),
      invalidatesTags: ['Emoji'],
    }),
    hlExportEmojiPack: builder.query({
      query: (packId) => `/hivelink/emoji-packs/${packId}/export`,
      transformResponse: (r) => r.data,
    }),
    hlImportEmojiPack: builder.mutation({
      query: (body) => ({ url: '/hivelink/emoji-packs/import', method: 'POST', body }),
      invalidatesTags: ['Emoji'],
      transformResponse: (r) => r.data,
    }),

    // ═══════════════════════════════════════════
    //  CHANNEL CATEGORIES
    // ═══════════════════════════════════════════
    hlCreateCategory: builder.mutation({
      query: (body) => ({ url: '/hivelink/categories', method: 'POST', body }),
      invalidatesTags: ['Channel'],
      transformResponse: (r) => r.data,
    }),
    hlListCategories: builder.query({
      query: () => '/hivelink/categories',
      providesTags: ['Channel'],
      transformResponse: (r) => r.data,
    }),
    hlUpdateCategory: builder.mutation({
      query: ({ categoryId, ...body }) => ({ url: `/hivelink/categories/${categoryId}`, method: 'PUT', body }),
      invalidatesTags: ['Channel'],
    }),
    hlDeleteCategory: builder.mutation({
      query: (categoryId) => ({ url: `/hivelink/categories/${categoryId}`, method: 'DELETE' }),
      invalidatesTags: ['Channel'],
    }),
    hlReorderCategories: builder.mutation({
      query: (categoryIds) => ({ url: '/hivelink/categories/reorder', method: 'PATCH', body: { categoryIds } }),
      invalidatesTags: ['Channel'],
    }),
    hlSetChannelCategory: builder.mutation({
      query: ({ channelId, categoryId }) => ({
        url: `/hivelink/channels/${channelId}/category`, method: 'PATCH', body: { categoryId },
      }),
      invalidatesTags: ['Channel'],
    }),

    // ═══════════════════════════════════════════
    //  USER GROUPS
    // ═══════════════════════════════════════════
    hlCreateUserGroup: builder.mutation({
      query: (body) => ({ url: '/hivelink/user-groups', method: 'POST', body }),
      invalidatesTags: ['Channel'],
      transformResponse: (r) => r.data,
    }),
    hlListUserGroups: builder.query({
      query: (params) => ({ url: '/hivelink/user-groups', params }),
      providesTags: ['Channel'],
      transformResponse: (r) => r.data,
    }),
    hlUpdateUserGroup: builder.mutation({
      query: ({ groupId, ...body }) => ({ url: `/hivelink/user-groups/${groupId}`, method: 'PUT', body }),
      invalidatesTags: ['Channel'],
    }),
    hlDeleteUserGroup: builder.mutation({
      query: (groupId) => ({ url: `/hivelink/user-groups/${groupId}`, method: 'DELETE' }),
      invalidatesTags: ['Channel'],
    }),
    hlAddGroupMembers: builder.mutation({
      query: ({ groupId, memberIds }) => ({ url: `/hivelink/user-groups/${groupId}/members`, method: 'POST', body: { memberIds } }),
      invalidatesTags: ['Channel'],
    }),
    hlRemoveGroupMember: builder.mutation({
      query: ({ groupId, memberId }) => ({ url: `/hivelink/user-groups/${groupId}/members/${memberId}`, method: 'DELETE' }),
      invalidatesTags: ['Channel'],
    }),

    // ═══════════════════════════════════════════
    //  SAVED REPLIES
    // ═══════════════════════════════════════════
    hlCreateSavedReply: builder.mutation({
      query: (body) => ({ url: '/hivelink/saved-replies', method: 'POST', body }),
      invalidatesTags: ['SavedReply'],
      transformResponse: (r) => r.data,
    }),
    hlListSavedReplies: builder.query({
      query: (params) => ({ url: '/hivelink/saved-replies', params }),
      providesTags: ['SavedReply'],
      transformResponse: (r) => r.data,
    }),
    hlUpdateSavedReply: builder.mutation({
      query: ({ replyId, ...body }) => ({ url: `/hivelink/saved-replies/${replyId}`, method: 'PUT', body }),
      invalidatesTags: ['SavedReply'],
    }),
    hlDeleteSavedReply: builder.mutation({
      query: (replyId) => ({ url: `/hivelink/saved-replies/${replyId}`, method: 'DELETE' }),
      invalidatesTags: ['SavedReply'],
    }),
    hlUseSavedReply: builder.mutation({
      query: (replyId) => ({ url: `/hivelink/saved-replies/${replyId}/use`, method: 'POST' }),
    }),

    // ═══════════════════════════════════════════
    //  VOICE / VIDEO CALLS
    // ═══════════════════════════════════════════
    hlInitiateCall: builder.mutation({
      query: ({ channelId, ...body }) => ({ url: `/hivelink/channels/${channelId}/calls`, method: 'POST', body }),
      invalidatesTags: ['Call'],
      transformResponse: (r) => r.data,
    }),
    hlAnswerCall: builder.mutation({
      query: (callId) => ({ url: `/hivelink/calls/${callId}/answer`, method: 'POST' }),
      invalidatesTags: ['Call'],
      transformResponse: (r) => r.data,
    }),
    hlEndCall: builder.mutation({
      query: (callId) => ({ url: `/hivelink/calls/${callId}/end`, method: 'POST' }),
      invalidatesTags: ['Call'],
      transformResponse: (r) => r.data,
    }),
    hlLeaveCall: builder.mutation({
      query: (callId) => ({ url: `/hivelink/calls/${callId}/leave`, method: 'POST' }),
      invalidatesTags: ['Call'],
      transformResponse: (r) => r.data,
    }),
    hlStartRecording: builder.mutation({
      query: (callId) => ({ url: `/hivelink/calls/${callId}/recording/start`, method: 'POST' }),
      invalidatesTags: ['Call'],
      transformResponse: (r) => r.data,
    }),
    hlStopRecording: builder.mutation({
      query: ({ callId, ...body }) => ({ url: `/hivelink/calls/${callId}/recording/stop`, method: 'POST', body }),
      invalidatesTags: ['Call'],
      transformResponse: (r) => r.data,
    }),
    hlListCallHistory: builder.query({
      query: ({ channelId, ...params }) => ({ url: `/hivelink/channels/${channelId}/calls/history`, params }),
      providesTags: ['Call'],
      transformResponse: (r) => r.data,
    }),
    hlGetActiveCall: builder.query({
      query: (channelId) => `/hivelink/channels/${channelId}/calls/active`,
      providesTags: ['Call'],
      transformResponse: (r) => r.data,
    }),

    // ═══════════════════════════════════════════
    //  HUDDLES (LiveKit)
    // ═══════════════════════════════════════════

    // The only source of a LiveKit credential. Deliberately a mutation rather
    // than a query: a token must never be cached, or RTK Query would hand it to
    // whoever opens the panel next in the same browser session.
    hlHuddleToken: builder.mutation({
      query: ({ channelId, type }) => ({
        url: `/hivelink/channels/${channelId}/huddle/token`,
        method: 'POST',
        body: { type },
      }),
      transformResponse: (r) => r.data,
    }),

    hlHuddleMute: builder.mutation({
      query: ({ callId, targetUserId, trackSid, muted = true, reason }) => ({
        url: `/hivelink/huddles/${callId}/mute`,
        method: 'POST',
        body: { targetUserId, trackSid, muted, reason },
      }),
      transformResponse: (r) => r.data,
    }),

    hlHuddleRemove: builder.mutation({
      query: ({ callId, targetUserId, reason }) => ({
        url: `/hivelink/huddles/${callId}/remove`,
        method: 'POST',
        body: { targetUserId, reason },
      }),
      transformResponse: (r) => r.data,
    }),

    hlHuddleEnd: builder.mutation({
      query: ({ callId }) => ({ url: `/hivelink/huddles/${callId}/end`, method: 'POST' }),
      invalidatesTags: ['Call'],
      transformResponse: (r) => r.data,
    }),

    hlHuddleRecordingRequest: builder.mutation({
      query: ({ callId }) => ({ url: `/hivelink/huddles/${callId}/recording/request`, method: 'POST' }),
      transformResponse: (r) => r.data,
    }),

    hlHuddleRecordingConsent: builder.mutation({
      query: ({ callId, decision }) => ({
        url: `/hivelink/huddles/${callId}/recording/consent`,
        method: 'POST',
        body: { decision },
      }),
      transformResponse: (r) => r.data,
    }),

    hlHuddleRecordingStop: builder.mutation({
      query: ({ callId }) => ({ url: `/hivelink/huddles/${callId}/recording/stop`, method: 'POST' }),
      invalidatesTags: ['Call'],
      transformResponse: (r) => r.data,
    }),

    // ═══════════════════════════════════════════
    //  CHANNEL ANALYTICS
    // ═══════════════════════════════════════════
    hlGetChannelAnalytics: builder.query({
      query: ({ channelId, ...params }) => ({ url: `/hivelink/channels/${channelId}/analytics`, params }),
      transformResponse: (r) => r.data,
    }),

    // ═══════════════════════════════════════════
    //  SHARED CHANNELS
    // ═══════════════════════════════════════════
    hlCreateShareInvite: builder.mutation({
      query: ({ channelId, ...body }) => ({ url: `/hivelink/channels/${channelId}/share`, method: 'POST', body }),
      invalidatesTags: ['SharedChannel'],
      transformResponse: (r) => r.data,
    }),
    hlAcceptShareInvite: builder.mutation({
      query: (token) => ({ url: `/hivelink/shared/accept/${token}`, method: 'POST' }),
      invalidatesTags: ['SharedChannel', 'Channel'],
      transformResponse: (r) => r.data,
    }),
    hlListSharedChannels: builder.query({
      query: () => '/hivelink/shared',
      providesTags: ['SharedChannel'],
      transformResponse: (r) => r.data,
    }),
    hlGetSharedChannel: builder.query({
      query: (shareId) => `/hivelink/shared/${shareId}`,
      providesTags: ['SharedChannel'],
      transformResponse: (r) => r.data,
    }),
    hlUpdateSharePerms: builder.mutation({
      query: ({ shareId, ...body }) => ({ url: `/hivelink/shared/${shareId}/permissions`, method: 'PATCH', body }),
      invalidatesTags: ['SharedChannel'],
    }),
    hlRevokeShare: builder.mutation({
      query: (shareId) => ({ url: `/hivelink/shared/${shareId}/revoke`, method: 'POST' }),
      invalidatesTags: ['SharedChannel'],
    }),
    hlListPendingInvites: builder.query({
      query: () => '/hivelink/shared/pending',
      providesTags: ['SharedChannel'],
      transformResponse: (r) => r.data,
    }),

    // ═══════════════════════════════════════════
    //  AI FEATURES (10)
    // ═══════════════════════════════════════════
    hlAiChannelSummary: builder.mutation({
      query: (body) => ({ url: '/hivelink/ai/channel-summary', method: 'POST', body }),
      transformResponse: (r) => r.data,
    }),
    hlAiDailyRecap: builder.query({
      query: () => '/hivelink/ai/daily-recap',
      transformResponse: (r) => r.data,
    }),
    hlAiNlSearch: builder.mutation({
      query: (body) => ({ url: '/hivelink/ai/nl-search', method: 'POST', body }),
      transformResponse: (r) => r.data,
    }),
    hlAiExtractDecisions: builder.mutation({
      query: (body) => ({ url: '/hivelink/ai/extract-decisions', method: 'POST', body }),
      transformResponse: (r) => r.data,
    }),
    hlAiExtractActions: builder.mutation({
      query: (body) => ({ url: '/hivelink/ai/extract-actions', method: 'POST', body }),
      transformResponse: (r) => r.data,
    }),
    hlAiExtractMessageActions: builder.mutation({
      query: (body) => ({ url: '/hivelink/ai/extract-message-actions', method: 'POST', body }),
      transformResponse: (r) => r.data,
    }),
    hlAiToxicityCheck: builder.mutation({
      query: (body) => ({ url: '/hivelink/ai/toxicity-check', method: 'POST', body }),
      transformResponse: (r) => r.data,
    }),
    hlAiSentiment: builder.mutation({
      query: (body) => ({ url: '/hivelink/ai/sentiment', method: 'POST', body }),
      transformResponse: (r) => r.data,
    }),
    hlAiClusterNotifications: builder.mutation({
      query: () => ({ url: '/hivelink/ai/cluster-notifications', method: 'POST' }),
      transformResponse: (r) => r.data,
    }),
    hlAiEnableDeepWork: builder.mutation({
      query: (body) => ({ url: '/hivelink/ai/deep-work/enable', method: 'POST', body }),
      transformResponse: (r) => r.data,
    }),
    hlAiDisableDeepWork: builder.mutation({
      query: () => ({ url: '/hivelink/ai/deep-work/disable', method: 'POST' }),
      transformResponse: (r) => r.data,
    }),
    hlAiDeepWorkStatus: builder.query({
      query: () => '/hivelink/ai/deep-work/status',
      transformResponse: (r) => r.data,
      providesTags: ['DeepWork'],
    }),
    hlAiShouldInterrupt: builder.mutation({
      query: (body) => ({ url: '/hivelink/ai/should-interrupt', method: 'POST', body }),
      transformResponse: (r) => r.data,
    }),
  }),
});

export const {
  useHlJoinChannelMutation, useHlLeaveChannelMutation,
  useHlListMyCanvasesQuery, useLazyHlListMyCanvasesQuery,
  useHlListCanvasTemplatesQuery, useHlCreateCanvasTemplateMutation,
  useHlUpdateCanvasTemplateMutation, useHlDeleteCanvasTemplateMutation,
  useHlUseCanvasTemplateMutation,
  useHlListMyPollsQuery, useLazyHlListMyPollsQuery,
  useHlGetAISettingsQuery, useHlUpdateAISettingsMutation,
  useHlUpdateDeepWorkMutation, useHlLeaveAllChannelsMutation,
  useHlExportMyDataQuery, useLazyHlExportMyDataQuery,
  useHlResetMyKeysMutation, useHlDeleteMyDataMutation,

  // Canvas
  useHlListCanvasesQuery, useLazyHlListCanvasesQuery,
  useHlGetCanvasQuery, useLazyHlGetCanvasQuery,
  useHlCreateCanvasMutation, useHlUpdateCanvasMutation,
  useHlToggleCanvasItemMutation, useHlDeleteCanvasMutation,

  // Channel invitation templates
  useHlListInviteTemplatesQuery, useLazyHlListInviteTemplatesQuery,
  useHlCreateInviteTemplateMutation, useHlUpdateInviteTemplateMutation,
  useHlDeleteInviteTemplateMutation, useHlUseInviteTemplateMutation,

  useHlListChannelsQuery, useHlGetChannelQuery, useHlCreateChannelMutation,
  useHlUpdateChannelMutation, useHlDeleteChannelMutation,
  useHlArchiveChannelMutation, useHlUnarchiveChannelMutation,
  useHlListArchivedChannelsQuery,
  useHlAddMembersMutation, useHlRemoveMemberMutation, useHlUpdateMemberRoleMutation, useHlInviteByEmailMutation, useHlToggleStarMutation,
  useHlUpdateNotificationsMutation,
  useHlGenerateExternalLinkMutation, useHlRevokeExternalLinkMutation,
  useHlEnableE2EMutation, useHlDisableE2EMutation, useHlUploadE2EKeyMutation,
  useHlGetE2EKeysQuery, useLazyHlGetE2EKeysQuery, useHlRotateE2EKeyMutation,
  useHlGetMessagesQuery, useHlSendMessageMutation, useHlEditMessageMutation,
  useHlDeleteMessageMutation, useHlGetThreadQuery,
  useHlLinkEntityMutation, useHlUnlinkEntityMutation,
  useHlGetProjectContextQuery,
  useHlZunoResolveMutation,
  useHlForwardMessageMutation,
  useHlGetObjectCardQuery, useLazyHlGetObjectCardQuery,
  useHlMarkUnreadMutation,
  useHlAddChannelBookmarkMutation, useHlRemoveChannelBookmarkMutation, useHlGetThreadsInboxQuery,
  useLazyHlGetSmartRepliesQuery, useLazyHlCatchMeUpQuery,
  useLazyHlSearchProjectsQuery, useHlPostProjectBriefMutation,
  useHlGetHuddleComplianceQuery, useHlSetHuddleLegalHoldMutation,
  useHlToggleReactionMutation, useHlPinMessageMutation, useHlGetPinnedMessagesQuery,
  useHlToggleBookmarkMutation, useHlGetBookmarksQuery,
  useHlMarkReadMutation, useHlGetUnreadCountsQuery,
  useHlSearchMessagesQuery, useLazyHlSearchMessagesQuery,
  useHlSetPresenceMutation, useHlGetBulkPresenceMutation,
  useHlUploadFilesMutation, useHlTranslateMessageMutation,
  useHlSetReminderMutation, useHlGetRemindersQuery,
  useHlListNotificationsQuery, useHlMarkNotificationReadMutation, useHlMarkAllNotificationsReadMutation,
  // Scheduling
  useHlScheduleMessageMutation, useHlListScheduledMessagesQuery, useLazyHlListScheduledMessagesQuery,
  useHlUpdateScheduledMessageMutation, useHlCancelScheduledMessageMutation,
  // Categories
  useHlCreateCategoryMutation, useHlListCategoriesQuery,
  useHlUpdateCategoryMutation, useHlDeleteCategoryMutation,
  useHlReorderCategoriesMutation, useHlSetChannelCategoryMutation,
  // Polls
  useHlCreatePollMutation, useHlListPollsQuery, useLazyHlListPollsQuery,
  useHlGetPollQuery, useLazyHlGetPollQuery,
  useHlVotePollMutation, useHlAddPollOptionMutation, useHlClosePollMutation, useHlDeletePollMutation,
  // Custom Emojis
  useHlUploadEmojiMutation, useHlListEmojisQuery, useLazyHlListEmojisQuery,
  useHlGetEmojiCategoriesQuery, useHlUpdateEmojiMutation, useHlDeleteEmojiMutation,
  useHlCreateEmojiPackMutation, useHlListEmojiPacksQuery,
  useHlUpdateEmojiPackMutation, useHlDeleteEmojiPackMutation,
  useLazyHlExportEmojiPackQuery, useHlImportEmojiPackMutation,
  // User Groups
  useHlCreateUserGroupMutation, useHlListUserGroupsQuery,
  useHlUpdateUserGroupMutation, useHlDeleteUserGroupMutation,
  useHlAddGroupMembersMutation, useHlRemoveGroupMemberMutation,
  // Saved Replies
  useHlCreateSavedReplyMutation, useHlListSavedRepliesQuery,
  useHlUpdateSavedReplyMutation, useHlDeleteSavedReplyMutation,
  useHlUseSavedReplyMutation,
  // Calls
  useHlInitiateCallMutation, useHlAnswerCallMutation, useHlEndCallMutation,
  useHlLeaveCallMutation, useHlStartRecordingMutation, useHlStopRecordingMutation,
  useHlListCallHistoryQuery, useHlGetActiveCallQuery,
  // Huddles (LiveKit)
  useHlHuddleTokenMutation,
  useHlHuddleMuteMutation, useHlHuddleRemoveMutation, useHlHuddleEndMutation,
  useHlHuddleRecordingRequestMutation, useHlHuddleRecordingConsentMutation,
  useHlHuddleRecordingStopMutation,
  // Channel Analytics
  useHlGetChannelAnalyticsQuery, useLazyHlGetChannelAnalyticsQuery,
  // Shared Channels
  useHlCreateShareInviteMutation, useHlAcceptShareInviteMutation,
  useHlListSharedChannelsQuery, useHlGetSharedChannelQuery,
  useHlUpdateSharePermsMutation, useHlRevokeShareMutation, useHlListPendingInvitesQuery,
  // AI Features
  useHlAiChannelSummaryMutation, useHlAiDailyRecapQuery, useLazyHlAiDailyRecapQuery,
  useHlAiNlSearchMutation, useHlAiExtractDecisionsMutation, useHlAiExtractActionsMutation, useHlAiExtractMessageActionsMutation,
  useHlAiToxicityCheckMutation, useHlAiSentimentMutation, useHlAiClusterNotificationsMutation,
  useHlAiEnableDeepWorkMutation, useHlAiDisableDeepWorkMutation,
  useHlAiDeepWorkStatusQuery, useLazyHlAiDeepWorkStatusQuery,
  useHlAiShouldInterruptMutation,
} = hiveLinkApi;